import { Sparkles, TrendingUp, TrendingDown, Users, AlertTriangle } from "lucide-react";

const GRADE_COLORS = {
    Excellent: { from: "#0F766E", to: "#0EA5E9", text: "#FFFFFF" },
    "Very Good": { from: "#10B981", to: "#06B6D4", text: "#FFFFFF" },
    Good: { from: "#3B82F6", to: "#6366F1", text: "#FFFFFF" },
    Fair: { from: "#F59E0B", to: "#F97316", text: "#FFFFFF" },
    Poor: { from: "#E11D48", to: "#9F1239", text: "#FFFFFF" },
    "—": { from: "#94A3B8", to: "#64748B", text: "#FFFFFF" },
};

export default function GroupScoreCard({ score, grade, breakdown, compact, t }) {
    if (!score) return null;
    const colors = GRADE_COLORS[grade] || GRADE_COLORS["—"];
    if (compact) {
        return (
            <div
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-white shadow-sm"
                style={{ background: `linear-gradient(135deg, ${colors.from}, ${colors.to})` }}
                data-testid="group-score-chip"
            >
                <Users size={9} /> {score}
            </div>
        );
    }
    const b = breakdown || {};
    const onTimeBonus = b.on_time_multiplier > 1.0;
    const onTimePenalty = b.on_time_multiplier < 1.0;
    return (
        <div
            className="rounded-2xl p-4 text-white relative overflow-hidden product-shadow"
            style={{ background: `linear-gradient(135deg, ${colors.from}, ${colors.to})` }}
            data-testid="group-score-card"
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/80 font-bold flex items-center gap-1">
                        <Users size={11} /> {t ? t("splitpay.group_score.title") : "Group Credit DNA"}
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                        <div className="font-display text-4xl font-bold leading-none">{score}</div>
                        <div className="text-sm font-semibold text-white/90">{grade}</div>
                    </div>
                </div>
                <div className="size-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <Sparkles size={20} />
                </div>
            </div>

            <div className="mt-3 pt-3 border-t border-white/15 grid grid-cols-2 gap-3 text-[10px]">
                <Stat
                    label={t ? t("splitpay.group_score.avg") : "Member avg"}
                    value={b.weighted_avg ? Math.round(b.weighted_avg) : "—"}
                />
                <Stat
                    label={t ? t("splitpay.group_score.spread") : "Tier spread"}
                    value={b.tier_spread !== undefined ? b.tier_spread : "—"}
                    warn={b.tier_spread_penalty > 0}
                />
            </div>

            <div className="mt-2 space-y-1 text-[10.5px] text-white/85">
                {onTimeBonus && (
                    <div className="flex items-center gap-1.5"><TrendingUp size={11} /> {t ? t("splitpay.group_score.on_time_bonus", { mult: (b.on_time_multiplier * 100).toFixed(0) }) : `+${Math.round((b.on_time_multiplier - 1) * 100)}% on-time bonus`}</div>
                )}
                {onTimePenalty && (
                    <div className="flex items-center gap-1.5"><TrendingDown size={11} /> {t ? t("splitpay.group_score.overdue_penalty", { count: b.overdue_count || 0 }) : `${b.overdue_count} member(s) overdue`}</div>
                )}
                {b.tier_spread_penalty > 0 && (
                    <div className="flex items-center gap-1.5"><AlertTriangle size={11} /> {t ? t("splitpay.group_score.spread_penalty", { pts: b.tier_spread_penalty }) : `−${b.tier_spread_penalty} pts: wide tier spread`}</div>
                )}
                {(!onTimeBonus && !onTimePenalty && !b.tier_spread_penalty) && (
                    <div className="flex items-center gap-1.5"><Sparkles size={11} /> {t ? t("splitpay.group_score.healthy") : "Healthy group · everyone aligned"}</div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, warn }) {
    return (
        <div className={warn ? "" : ""}>
            <div className="text-white/70 font-bold uppercase tracking-wider">{label}</div>
            <div className={`font-display text-lg font-bold mt-0.5 ${warn ? "text-amber-200" : ""}`}>{value}</div>
        </div>
    );
}
